import { ChangeEvent, DragEvent, useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, X } from "lucide-react";
import { DocumentUpload } from "iconsax-reactjs";
import { toast } from "sonner";
import {
  Button,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalTitle,
} from "../../../shared/ui";
import {
  Select as ShadSelect,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../shared/ui/shadcnSelect";
import { uploadKybDocumentFile } from "../../../shared/api/kyb";
import { AddressFormData, DirectorFormData, KybRequirement } from "../types";

type ReEnterInformationMode =
  | "address"
  | "director"
  | "identification"
  | "plain";

type KybComplianceReEnterInformationModalProps = {
  open: boolean;
  requirement: KybRequirement | null;
  targetId?: string;
  isSubmitting: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (value: string | AddressFormData | DirectorFormData) => void;
};

const MAX_FILE_SIZE_BYTES = 5 * 1024 * 1024;
const ACCEPTED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/jpg",
  "application/pdf",
];

// ─── Options ─────────────────────────────────────────────────────────────────

const identificationOptions = [
  { label: "BVN", value: "BVN" },
  { label: "Passport", value: "Passport" },
  { label: "National ID", value: "National ID" },
  { label: "Driver's license", value: "Driver's license" },
];

const directorIdTypeOptions = [
  { label: "National ID", value: "nationalId" },
  { label: "International Passport", value: "internationalPassport" },
  { label: "Driver's License", value: "driversLicense" },
];

const titleOptions = [
  { label: "Mr", value: "Mr" },
  { label: "Mrs", value: "Mrs" },
  { label: "Miss", value: "Miss" },
  { label: "Dr", value: "Dr" },
  { label: "Prof", value: "Prof" },
];

const genderOptions = [
  { label: "Male", value: "Male" },
  { label: "Female", value: "Female" },
];

// ─── Mode detection ──────────────────────────────────────────────────────────

const getRequirementMode = (
  requirement: KybRequirement | null,
): ReEnterInformationMode => {
  const key = (requirement?.rawKey || requirement?.key || "").toLowerCase();

  if (key.includes("address")) return "address";

  if (
    key.includes("identification") ||
    key.includes("passport") ||
    key.includes("national_id") ||
    key.includes("national id") ||
    key.includes("bvn")
  ) {
    return "identification";
  }

  if (key.includes("director") || key.includes("ubo")) return "director";

  return "plain";
};

// ─── Shared input primitive ───────────────────────────────────────────────────

const ReEnterInput = ({
  className = "",
  disabled,
  onChange,
  placeholder,
  value,
}: {
  className?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  placeholder: string;
  value: string;
}) => (
  <input
    type="text"
    disabled={disabled}
    value={value}
    onChange={(event) => onChange(event.target.value)}
    placeholder={placeholder}
    className={`h-12 w-full rounded-[14px] bg-[#EEF1FB] px-4 py-3 text-[14px] font-[350] text-gray-30 placeholder:text-gray-20 focus:outline-none focus:ring-2 focus:ring-[#D9DEFF] disabled:cursor-not-allowed disabled:opacity-60 ${className}`}
  />
);

// ─── Dropdown (shadcnSelect styled to match inputs) ───────────────────────────

const ReEnterDropdown = ({
  className = "",
  disabled,
  onChange,
  options,
  placeholder,
  value,
}: {
  className?: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  value: string;
}) => (
  <ShadSelect value={value} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger
      className={`h-12 w-full rounded-[14px] border-0 bg-[#EEF1FB] px-4 text-[14px] font-[350] text-gray-30 shadow-none focus:ring-2 focus:ring-[#D9DEFF] focus-visible:ring-2 focus-visible:ring-[#D9DEFF] disabled:cursor-not-allowed disabled:opacity-60 data-[size=default]:h-12 ${className}`}
    >
      <SelectValue placeholder={placeholder ?? "Select…"} />
    </SelectTrigger>
    <SelectContent className="z-[200] border border-[#E6E9F5] bg-white shadow-lg">
      <SelectGroup>
        {options.map((opt) => (
          <SelectItem
            key={opt.value}
            value={opt.value}
            className="cursor-pointer text-[14px] text-[#2C2F44] focus:bg-[#EEF1FB]"
          >
            {opt.label}
          </SelectItem>
        ))}
      </SelectGroup>
    </SelectContent>
  </ShadSelect>
);

// ─── Section label ────────────────────────────────────────────────────────────

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
  <div className="aku-body-small-medium text-[#2C2F44]">{children}</div>
);

// ─── Upload tile (matches KybComplianceReUploadDocumentModal exactly) ─────────

type UploadedDoc = { url: string; key: string };

const UploadTile = ({
  label,
  disabled,
  uploaded,
  uploading,
  onFileSelected,
}: {
  label: string;
  disabled?: boolean;
  uploaded: UploadedDoc | null;
  uploading: boolean;
  onFileSelected: (file: File) => void;
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState("");

  const selectFile = (file?: File | null) => {
    if (!file) return;
    if (!ACCEPTED_FILE_TYPES.includes(file.type)) {
      setSelectedFile(null);
      setFileError("Upload a JPEG, PNG, JPG, or PDF file.");
      return;
    }
    if (file.size > MAX_FILE_SIZE_BYTES) {
      setSelectedFile(null);
      setFileError("File size must be 5 MB or less.");
      return;
    }
    setFileError("");
    setSelectedFile(file);
    onFileSelected(file);
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) =>
    selectFile(e.target.files?.[0]);

  const handleDrop = (e: DragEvent<HTMLButtonElement>) => {
    e.preventDefault();
    selectFile(e.dataTransfer.files?.[0]);
  };

  // Reset local file state when parent clears uploaded doc (e.g. on ID type change)
  useEffect(() => {
    if (!uploaded) {
      setSelectedFile(null);
      setFileError("");
      if (inputRef.current) inputRef.current.value = "";
    }
  }, [uploaded]);

  return (
    <div>
      <div className="aku-body-small-medium text-[#2C2F44]">{label}</div>

      <input
        ref={inputRef}
        type="file"
        accept=".jpeg,.jpg,.png,.pdf,image/jpeg,image/png,application/pdf"
        className="hidden"
        disabled={disabled || uploading}
        onChange={handleFileChange}
      />

      <button
        type="button"
        disabled={disabled || uploading}
        onClick={() => inputRef.current?.click()}
        onDragOver={(event) => event.preventDefault()}
        onDrop={handleDrop}
        className="mt-4 flex w-full items-center gap-5 rounded-[14px] border border-dashed border-[#DDE2FA] bg-[#EEF1FB] px-4 py-3 text-left transition-colors hover:bg-[#E9ECF8] disabled:cursor-not-allowed disabled:opacity-60"
      >
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-[#D7C5FF] text-[#6F00FF]">
          <DocumentUpload size={26} color="#6F00FF" variant="Bold" />
        </span>
        <span className="min-w-0">
          <span className="block aku-body-normal-medium text-[#161827]">
            {uploading
              ? "Uploading…"
              : selectedFile?.name || "Drag or Upload Document"}
          </span>
          <span className="mt-1 block aku-body-small-regular text-[#6F739B]">
            JPEG, PNG, JPG, PDF Max. file size: 5.0 MB
          </span>
        </span>
      </button>

      {fileError ? (
        <p className="mt-2 aku-body-small-regular text-[#C24E4E]">{fileError}</p>
      ) : null}
    </div>
  );
};



const KybComplianceReEnterInformationModal = ({
  open,
  requirement,
  targetId,
  isSubmitting,
  onOpenChange,
  onSubmit,
}: KybComplianceReEnterInformationModalProps) => {
  const mode = useMemo(() => getRequirementMode(requirement), [requirement]);

  const [plainValue, setPlainValue] = useState("");
  const [houseNumber, setHouseNumber] = useState("");
  const [street, setStreet] = useState("");
  const [city, setCity] = useState("");
  const [addrState, setAddrState] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [subdivision, setSubdivision] = useState("");
  const [identificationType, setIdentificationType] = useState("BVN");
  const [identificationNumber, setIdentificationNumber] = useState("");

  const [dirTitle, setDirTitle] = useState("Mr");
  const [dirFirstName, setDirFirstName] = useState("");
  const [dirLastName, setDirLastName] = useState("");
  const [dirEmail, setDirEmail] = useState("");
  const [dirPhone, setDirPhone] = useState("");
  const [dirGender, setDirGender] = useState("Male");
  const [dirIdType, setDirIdType] = useState("nationalId");
  const [dirIdNumber, setDirIdNumber] = useState("");
  const [dirOwnership, setDirOwnership] = useState("");
  const [dirHouseNumber, setDirHouseNumber] = useState("");
  const [dirStreet, setDirStreet] = useState("");
  const [dirCity, setDirCity] = useState("");
  const [dirState, setDirState] = useState("");
  const [dirZip, setDirZip] = useState("");
  const [dirSubdivision, setDirSubdivision] = useState("");

  const [idFrontDoc, setIdFrontDoc] = useState<UploadedDoc | null>(null);
  const [idBackDoc, setIdBackDoc] = useState<UploadedDoc | null>(null);
  const [idSingleDoc, setIdSingleDoc] = useState<UploadedDoc | null>(null);
  const [uploadingFront, setUploadingFront] = useState(false);
  const [uploadingBack, setUploadingBack] = useState(false);
  const [uploadingSingle, setUploadingSingle] = useState(false);

  const isAnyUploading = uploadingFront || uploadingBack || uploadingSingle;
  const isNationalId = dirIdType === "nationalId";

  const idTypeLabel =
    directorIdTypeOptions.find((o) => o.value === dirIdType)?.label ?? dirIdType;

  const resetState = () => {
    setPlainValue("");
    setHouseNumber(""); setStreet(""); setCity(""); setAddrState(""); setZipCode(""); setSubdivision("");
    setIdentificationType("BVN"); setIdentificationNumber("");
    setDirTitle("Mr"); setDirFirstName(""); setDirLastName("");
    setDirEmail(""); setDirPhone(""); setDirGender("Male");
    setDirIdType("nationalId"); setDirIdNumber(""); setDirOwnership("");
    setDirHouseNumber(""); setDirStreet(""); setDirCity("");
    setDirState(""); setDirZip(""); setDirSubdivision("");
    setIdFrontDoc(null); setIdBackDoc(null); setIdSingleDoc(null);
    setUploadingFront(false); setUploadingBack(false); setUploadingSingle(false);
  };

  useEffect(() => {
    if (!open) return;
    resetState();
    setPlainValue(requirement?.textValue ?? "");

    const obj = requirement?.objectValue;
    if (obj) {
      setHouseNumber(obj.houseNumber ?? "");
      setStreet(obj.street ?? "");
      setCity(obj.city ?? "");
      setAddrState(obj.state ?? "");
      setZipCode(obj.zipCode ?? "");
      setSubdivision(obj.subdivision ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, requirement]);

  useEffect(() => {
    setIdFrontDoc(null);
    setIdBackDoc(null);
    setIdSingleDoc(null);
  }, [dirIdType]);

  const uploadFile = async (
    file: File,
    setUploading: (v: boolean) => void,
    setDoc: (doc: UploadedDoc) => void,
  ) => {
    setUploading(true);
    try {
      const result = await uploadKybDocumentFile(file);
      if (!result.url) throw new Error("No URL returned from upload.");
      setDoc({ url: result.url, key: result.key || file.name });
    } catch {
      toast.error("Failed to upload document. Please try again.");
    } finally {
      setUploading(false);
    }
  };

  const directorFormData = useMemo<DirectorFormData>(
    () => ({
      targetId,
      title: dirTitle,
      firstName: dirFirstName.trim(),
      lastName: dirLastName.trim(),
      email: dirEmail.trim(),
      phoneNumber: dirPhone.trim(),
      gender: dirGender,
      meanOfIdentificationType: dirIdType,
      idNumber: dirIdNumber.trim(),
      percentageOwnership: dirOwnership.trim(),
      // For national ID: front/back doc objects; for others: single doc as meanOfIdentificationUrl
      meanOfIdentificationUrl: isNationalId ? undefined : idSingleDoc?.url,
      idDocumentFront: isNationalId ? (idFrontDoc ?? undefined) : undefined,
      idDocumentBack: isNationalId ? (idBackDoc ?? undefined) : undefined,
      address: {
        houseNumber: dirHouseNumber.trim(),
        street: dirStreet.trim(),
        city: dirCity.trim(),
        state: dirState.trim(),
        zipCode: dirZip.trim(),
        subdivision: dirSubdivision.trim(),
      },
    }),
    [
      targetId, dirTitle, dirFirstName, dirLastName, dirEmail, dirPhone,
      dirGender, dirIdType, dirIdNumber, dirOwnership, isNationalId,
      idFrontDoc, idBackDoc, idSingleDoc,
      dirHouseNumber, dirStreet, dirCity, dirState, dirZip, dirSubdivision,
    ],
  );

  const addressFormData = useMemo<AddressFormData>(
    () => ({
      houseNumber: houseNumber.trim(),
      street: street.trim(),
      city: city.trim(),
      state: addrState.trim(),
      zipCode: zipCode.trim(),
      subdivision: subdivision.trim(),
    }),
    [addrState, city, houseNumber, street, subdivision, zipCode],
  );

  const submitValue = useMemo(() => {
    if (mode === "address") {
      return addressFormData;
    }
    if (mode === "identification") {
      return `${identificationType}: ${identificationNumber.trim()}`;
    }
    return plainValue.trim();
  }, [addressFormData, identificationNumber, identificationType, mode, plainValue]);

  const canSubmit =
    mode === "address"
      ? Boolean(street.trim() && city.trim() && addrState.trim() && zipCode.trim())
      : mode === "director"
        ? Boolean(
          !isAnyUploading &&
          dirFirstName.trim() &&
          dirLastName.trim() &&
          dirEmail.trim() &&
          dirPhone.trim() &&
          dirIdNumber.trim() &&
          dirStreet.trim() &&
          dirCity.trim() &&
          dirState.trim() &&
          dirZip.trim(),
        )
        : mode === "identification"
          ? Boolean(identificationType.trim() && identificationNumber.trim())
          : Boolean(plainValue.trim());

  const handleSubmit = () => {
    if (mode === "director") {
      onSubmit(directorFormData);
    } else {
      onSubmit(submitValue);
    }
  };

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      overlayClassName="bg-black/75"
      contentClassName="max-w-[620px] bg-white"
    >
      <ModalHeader className="items-center aku-heading-h2-semibold">
        <ModalTitle>Re-enter information</ModalTitle>
        <button
          type="button"
          aria-label="Close modal"
          disabled={isSubmitting}
          onClick={() => onOpenChange(false)}
          className="rounded-full p-2 text-[#737AA8] hover:bg-[#F2F4FF] disabled:cursor-not-allowed disabled:opacity-50"
        >
          <X size={20} />
        </button>
      </ModalHeader>

      <ModalBody className="pt-4">
        {/* ── Address ── */}
        {mode === "address" ? (
          <div>
            <SectionLabel>Business address</SectionLabel>
            <div className="mt-2 space-y-3">
              <ReEnterInput
                disabled={isSubmitting}
                value={houseNumber}
                onChange={setHouseNumber}
                placeholder="House number"
              />
              <ReEnterInput
                disabled={isSubmitting}
                value={street}
                onChange={setStreet}
                placeholder="Street address"
              />
              <div className="grid gap-3 md:grid-cols-3">
                <ReEnterInput disabled={isSubmitting} value={city} onChange={setCity} placeholder="City" />
                <ReEnterInput disabled={isSubmitting} value={addrState} onChange={setAddrState} placeholder="State" />
                <ReEnterInput disabled={isSubmitting} value={zipCode} onChange={setZipCode} placeholder="ZIP code" />
              </div>
              <ReEnterInput
                disabled={isSubmitting}
                value={subdivision}
                onChange={setSubdivision}
                placeholder="Subdivision / LGA"
              />
            </div>
          </div>
        ) : null}

        {/* ── Director / UBO ── */}
        {mode === "director" ? (
          <div className="space-y-6">
            {/* Personal information */}
            <div>
              <SectionLabel>Personal information</SectionLabel>
              <div className="mt-2 space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <ReEnterDropdown
                    disabled={isSubmitting}
                    value={dirTitle}
                    onChange={setDirTitle}
                    options={titleOptions}
                    placeholder="Title"
                  />
                  <ReEnterDropdown
                    disabled={isSubmitting}
                    value={dirGender}
                    onChange={setDirGender}
                    options={genderOptions}
                    placeholder="Gender"
                  />
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <ReEnterInput disabled={isSubmitting} value={dirFirstName} onChange={setDirFirstName} placeholder="First name *" />
                  <ReEnterInput disabled={isSubmitting} value={dirLastName} onChange={setDirLastName} placeholder="Last name *" />
                </div>
                <ReEnterInput disabled={isSubmitting} value={dirEmail} onChange={setDirEmail} placeholder="Email address *" />
                <ReEnterInput disabled={isSubmitting} value={dirPhone} onChange={setDirPhone} placeholder="Phone number *" />
              </div>
            </div>

            {/* Identity */}
            <div>
              <SectionLabel>Identity</SectionLabel>
              <div className="mt-2 space-y-3">
                <ReEnterDropdown
                  disabled={isSubmitting}
                  value={dirIdType}
                  onChange={setDirIdType}
                  options={directorIdTypeOptions}
                  placeholder="ID type"
                />
                <ReEnterInput
                  disabled={isSubmitting}
                  value={dirIdNumber}
                  onChange={setDirIdNumber}
                  placeholder="ID number *"
                />

                {/* Document uploads — dynamic based on ID type */}
                {isNationalId ? (
                  <>
                    <UploadTile
                      label="National ID (Front)"
                      disabled={isSubmitting}
                      uploaded={idFrontDoc}
                      uploading={uploadingFront}
                      onFileSelected={(file) =>
                        uploadFile(file, setUploadingFront, setIdFrontDoc)
                      }
                    />
                    <UploadTile
                      label="National ID (Back)"
                      disabled={isSubmitting}
                      uploaded={idBackDoc}
                      uploading={uploadingBack}
                      onFileSelected={(file) =>
                        uploadFile(file, setUploadingBack, setIdBackDoc)
                      }
                    />
                  </>
                ) : (
                  <UploadTile
                    label={idTypeLabel}
                    disabled={isSubmitting}
                    uploaded={idSingleDoc}
                    uploading={uploadingSingle}
                    onFileSelected={(file) =>
                      uploadFile(file, setUploadingSingle, setIdSingleDoc)
                    }
                  />
                )}
              </div>
            </div>

            {/* Ownership */}
            <div>
              <SectionLabel>Ownership</SectionLabel>
              <ReEnterInput
                className="mt-2"
                disabled={isSubmitting}
                value={dirOwnership}
                onChange={setDirOwnership}
                placeholder="Percentage ownership (e.g. 25)"
              />
            </div>

            {/* Address */}
            <div>
              <SectionLabel>Residential address</SectionLabel>
              <div className="mt-2 space-y-3">
                <div className="grid gap-3 md:grid-cols-2">
                  <ReEnterInput disabled={isSubmitting} value={dirHouseNumber} onChange={setDirHouseNumber} placeholder="House number" />
                  <ReEnterInput disabled={isSubmitting} value={dirSubdivision} onChange={setDirSubdivision} placeholder="Subdivision / LGA" />
                </div>
                <ReEnterInput disabled={isSubmitting} value={dirStreet} onChange={setDirStreet} placeholder="Street address *" />
                <div className="grid gap-3 md:grid-cols-3">
                  <ReEnterInput disabled={isSubmitting} value={dirCity} onChange={setDirCity} placeholder="City *" />
                  <ReEnterInput disabled={isSubmitting} value={dirState} onChange={setDirState} placeholder="State *" />
                  <ReEnterInput disabled={isSubmitting} value={dirZip} onChange={setDirZip} placeholder="ZIP code *" />
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {/* ── Identification ── */}
        {mode === "identification" ? (
          <div>
            <SectionLabel>Identification Number</SectionLabel>
            <div className="mt-2 flex h-12 items-center rounded-[14px] bg-[#EEF1FB] px-4 focus-within:ring-2 focus-within:ring-[#D9DEFF]">
              <div className="relative shrink-0">
                <select
                  value={identificationType}
                  disabled={isSubmitting}
                  onChange={(event) => setIdentificationType(event.target.value)}
                  className="appearance-none bg-transparent pr-8 text-[14px] font-[450] text-[#2C2F44] outline-none disabled:cursor-not-allowed"
                >
                  {identificationOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <ChevronDown
                  size={16}
                  className="pointer-events-none absolute right-1 top-1/2 -translate-y-1/2 text-[#6F739B]"
                />
              </div>
              <input
                type="text"
                disabled={isSubmitting}
                value={identificationNumber}
                onChange={(event) => setIdentificationNumber(event.target.value)}
                placeholder="Enter identification number"
                className="min-w-0 flex-1 bg-transparent px-3 text-[14px] font-[350] text-gray-30 placeholder:text-gray-20 outline-none disabled:cursor-not-allowed"
              />
            </div>
          </div>
        ) : null}

        {/* ── Plain ── */}
        {mode === "plain" ? (
          <div>
            <SectionLabel>{requirement?.key || "Information"}</SectionLabel>
            <ReEnterInput
              className="mt-2"
              disabled={isSubmitting}
              value={plainValue}
              onChange={setPlainValue}
              placeholder="Enter value"
            />
          </div>
        ) : null}
      </ModalBody>

      <ModalFooter>
        <Button
          type="button"
          variant="ghost"
          disabled={isSubmitting || isAnyUploading}
          onClick={() => onOpenChange(false)}
          className="h-11 rounded-[14px] px-6 text-[#2C2F44]"
        >
          Cancel
        </Button>
        <Button
          type="button"
          disabled={!canSubmit || isSubmitting || isAnyUploading}
          onClick={handleSubmit}
          className="h-11 rounded-[14px] px-6 text-[16px] font-[500]"
        >
          {isSubmitting ? "Submitting…" : isAnyUploading ? "Uploading…" : "Submit"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default KybComplianceReEnterInformationModal;
